/**
 * Dynamic import helper - drop-in replacement for next/dynamic.
 *
 * Two-layer protection against network failures during chunk loading:
 *
 * 1. PRE-FLIGHT GATE: Before calling import(), we fetch() the origin to verify
 *    connectivity. fetch() has no permanent failure cache, so we can retry it
 *    forever. import() is only called after pre-flight succeeds, keeping the
 *    browser's ES module map clean.
 *
 * 2. RELOAD FALLBACK: If the rare race condition occurs (pre-flight passes,
 *    import() fails in the ms between), the module map is now permanently
 *    poisoned — no JS-level fix. The error boundary detects this and reloads
 *    the page automatically.
 *
 * Usage:
 *   import { dynamic } from "@/lib/dynamic";
 *   const MyComponent = dynamic(() => import("./MyComponent"), { loading: () => <Spinner /> });
 *
 *   // For named exports:
 *   const NamedComponent = dynamic(() => import("./MyComponent").then(mod => ({ default: mod.NamedExport })));
 *
 *   // For icon components (no default wrapper needed):
 *   const Icon = dynamic(() => import("lucide-react").then(mod => ({ default: mod.IconName })));
 */
import { lazy, Suspense, Component, ComponentType, ReactElement } from 'react';

interface DynamicOptions {
  /** Component to show while loading */
  loading?: () => ReactElement
  /** Ignored - kept for API compatibility with next/dynamic */
  ssr?: boolean;
}

type NormalizedImportFn = () => Promise<{ default: ComponentType<any> }>;

// ============================================================================
// PRE-FLIGHT — verify server is reachable before calling import()
// ============================================================================

/**
 * fetch() only throws on actual network errors (DNS failure, offline, etc.).
 * Any HTTP response (even 4xx/5xx) means the network path works and chunks
 * should be fetchable. No permanent failure cache — safe to retry forever.
 */
async function checkConnectivity(): Promise<void> {
  await fetch(window.location.origin, { method: 'HEAD', cache: 'no-store' });
}

/**
 * Detect if an error is from a failed ES module import (poisoned module map).
 * Covers Chrome, Firefox, and Safari error messages.
 */
function isModuleLoadError(error: unknown): boolean {
  const msg = String(error).toLowerCase();
  return (
    msg.includes('dynamically imported module') ||
    msg.includes('module script') ||
    msg.includes('importing a module')
  );
}

// ============================================================================
// ERROR BOUNDARY — retries pre-flight failures, reloads on poisoned module map
// ============================================================================

interface DynamicErrorBoundaryProps {
  importFn: NormalizedImportFn;
  Loading?: () => ReactElement;
  componentProps: Record<string, any>;
}

interface DynamicErrorBoundaryState {
  hasError: boolean;
  LazyComponent: ComponentType<any>;
  retryAttempt: number;
}

class DynamicErrorBoundary extends Component<DynamicErrorBoundaryProps, DynamicErrorBoundaryState> {
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: DynamicErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      LazyComponent: lazy(props.importFn),
      retryAttempt: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<DynamicErrorBoundaryState> {
    // Race condition: pre-flight passed but import() failed between checks.
    // Module map is permanently poisoned — only a page reload can fix this.
    if (isModuleLoadError(error)) {
      console.error('[dynamic] Module map poisoned, reloading page:', String(error));
      window.location.reload();
      return { hasError: true }; // Won't render — reload is in progress
    }
    // Pre-flight failure — module map is clean, safe to retry
    return { hasError: true };
  }

  componentDidUpdate(_prevProps: DynamicErrorBoundaryProps, prevState: DynamicErrorBoundaryState) {
    if (this.state.hasError && !prevState.hasError) {
      this.scheduleRetry();
    }
  }

  componentWillUnmount() {
    if (this.retryTimer) clearTimeout(this.retryTimer);
  }

  scheduleRetry = () => {
    const nextAttempt = this.state.retryAttempt + 1;
    const delay = Math.min(1000 * Math.pow(2, nextAttempt - 1), 5000);

    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.setState({
        hasError: false,
        retryAttempt: nextAttempt,
        LazyComponent: lazy(this.props.importFn),
      });
    }, delay);
  };

  retryNow = () => {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.setState({
      hasError: false,
      retryAttempt: this.state.retryAttempt + 1,
      LazyComponent: lazy(this.props.importFn),
    });
  };

  render() {
    const { Loading, componentProps } = this.props;
    const { hasError, LazyComponent, retryAttempt } = this.state;

    if (hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full p-4 gap-2">
          <div className="text-sm text-zinc-400">Waiting for connection…</div>
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <span>Retrying… (attempt {retryAttempt + 1})</span>
          </div>
          <button
            onClick={this.retryNow}
            className="text-xs px-3 py-1 mt-1 rounded border border-zinc-600 text-zinc-400 hover:bg-zinc-700/50 transition-colors cursor-pointer"
          >
            Retry Now
          </button>
        </div>
      );
    }

    return (
      <Suspense fallback={Loading ? <Loading /> : null}>
        <LazyComponent {...componentProps} />
      </Suspense>
    );
  }
}

// ============================================================================
// DYNAMIC IMPORT HELPER
// ============================================================================

/**
 * Dynamically import a component with lazy loading, pre-flight connectivity
 * check, and automatic retry/reload on failure.
 * Drop-in replacement for next/dynamic.
 */
export function dynamic<P = any>(
  importFn: () => Promise<{ default: ComponentType<P> } | ComponentType<P>>,
  options: DynamicOptions = {}
): ComponentType<P> {
  // Wrap importFn with pre-flight check and normalization.
  // Pre-flight verifies server is reachable BEFORE calling import(),
  // keeping the browser's ES module map clean on network failure.
  // Once the module loads successfully, skip pre-flight on subsequent mounts —
  // the browser's module cache handles it and import() resolves instantly.
  let loaded = false;

  const normalizeModule = (mod: { default: ComponentType<any> } | ComponentType<any>): { default: ComponentType<any> } => {
    if (mod && typeof mod === 'object' && 'default' in mod) {
      return mod as { default: ComponentType<any> };
    }
    return { default: mod as ComponentType<any> };
  };

  const guardedImport: NormalizedImportFn = () => {
    const doImport = () =>
      importFn().then((mod) => {
        loaded = true;
        return normalizeModule(mod);
      });

    if (loaded) return doImport();
    return checkConnectivity().then(doImport);
  };

  const { loading: Loading } = options;

  return function DynamicComponent(props: P & {}) {
    return (
      <DynamicErrorBoundary
        importFn={guardedImport}
        Loading={Loading}
        componentProps={props}
      />
    );
  } as ComponentType<P>;
}

export default dynamic;
